import * as Sentry from "@sentry/react";

/**
 * Sentry initialization for Her Season.
 *
 * Privacy posture (health app):
 *  - No Session Replay (avoids capturing sensitive health inputs).
 *  - No automatic PII collection (sendDefaultPii: false).
 *  - Performance tracing is enabled at a low sample rate but request/response
 *    bodies and query params are stripped in beforeSendTransaction.
 *  - beforeSend scrubs names, emails, health data, AI conversations, etc.
 *  - Users are identified only by an anonymous internal id (never name/email).
 */

// Keys anywhere in event/context/breadcrumb data whose values must be dropped.
const SENSITIVE_KEYS = [
  "name",
  "first_name",
  "last_name",
  "full_name",
  "display_name",
  "username",
  "email",
  "email_address",
  "phone",
  "phone_number",
  "address",
  "dob",
  "date_of_birth",
  "avatar_url",
  "profile",
  "profile_info",
  "profile_information",

  // Health domains specific to this app
  "symptom",
  "symptoms",
  "symptom_log",
  "symptom_logs",
  "menstrual",
  "menstrual_history",
  "period",
  "period_log",
  "period_logs",
  "flow",
  "cycle",
  "bladder",
  "bladder_diary",
  "sleep",
  "sleep_log",
  "sleep_data",
  "medication",
  "medications",
  "prescription",
  "conversation",
  "ai_conversation",
  "ai_message",
  "messages",
  "doctor_report",
  "doctor_reports",
  "notes",
  "diary",
  "journal",
  "health",
  "diagnosis",
  "condition",
];

const SENSITIVE_KEY_RE = new RegExp(
  `(${SENSITIVE_KEYS.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
  "i",
);

// Email / phone patterns for string scrubbing.
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const PHONE_RE = /\+?\d[\d\s().-]{7,}\d/g;

function redactString(s: string): string {
  return s.replace(EMAIL_RE, "[redacted-email]").replace(PHONE_RE, "[redacted-phone]");
}

function scrub(value: unknown, depth = 0): unknown {
  if (value == null || depth > 6) return value;
  if (typeof value === "string") return redactString(value);
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_KEY_RE.test(k)) {
        out[k] = "[redacted]";
      } else {
        out[k] = scrub(v, depth + 1);
      }
    }
    return out;
  }
  return value;
}

function resolveEnvironment(): "development" | "staging" | "production" {
  const explicit = import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined;
  if (explicit === "development" || explicit === "staging" || explicit === "production") {
    return explicit;
  }
  if (import.meta.env.DEV) return "development";
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host.includes("staging") || host.includes("preview") || host.includes("lovable.app")) {
      return "staging";
    }
  }
  return "production";
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info("[sentry] VITE_SENTRY_DSN not set — Sentry disabled.");
    }
    return;
  }

  const environment = resolveEnvironment();

  Sentry.init({
    dsn,
    environment,
    release: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? undefined,

    // Never send default PII (IP, cookies, headers, user-agent-derived identity).
    sendDefaultPii: false,

    // Performance tracing — low sample; bodies scrubbed in beforeSendTransaction.
    tracesSampleRate: environment === "production" ? 0.1 : 0.5,

    // NO Session Replay — health inputs cannot be guaranteed masked.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    integrations: [
      Sentry.browserTracingIntegration(),
      // Reduce breadcrumb chattiness that could carry PII.
      Sentry.breadcrumbsIntegration({
        console: false,
        dom: { serializeAttribute: [] },
        fetch: true,
        history: true,
        xhr: true,
      }),
    ],

    beforeBreadcrumb(breadcrumb) {
      // Strip request bodies from network breadcrumbs; keep URL path only.
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        if (breadcrumb.data) {
          delete (breadcrumb.data as Record<string, unknown>).request_body;
          delete (breadcrumb.data as Record<string, unknown>).response_body;
          const url = (breadcrumb.data as Record<string, unknown>).url;
          if (typeof url === "string") {
            try {
              const u = new URL(url, window.location.origin);
              (breadcrumb.data as Record<string, unknown>).url = `${u.origin}${u.pathname}`;
            } catch {
              /* ignore */
            }
          }
        }
      }
      if (breadcrumb.message) breadcrumb.message = redactString(breadcrumb.message);
      breadcrumb.data = scrub(breadcrumb.data) as typeof breadcrumb.data;
      return breadcrumb;
    },

    beforeSend(event) {
      // Strip request body, cookies, headers, query string values.
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        delete event.request.headers;
        if (event.request.url) {
          try {
            const u = new URL(event.request.url);
            event.request.url = `${u.origin}${u.pathname}`;
          } catch {
            /* ignore */
          }
        }
        delete event.request.query_string;
      }

      // Anonymize user — keep only an opaque id.
      if (event.user) {
        const id = event.user.id;
        event.user = id ? { id: String(id) } : undefined;
      }

      // Recursively scrub extra / contexts / tags / breadcrumb data.
      if (event.extra) event.extra = scrub(event.extra) as typeof event.extra;
      if (event.contexts) event.contexts = scrub(event.contexts) as typeof event.contexts;
      if (event.tags) event.tags = scrub(event.tags) as typeof event.tags;
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) => ({
          ...b,
          message: b.message ? redactString(b.message) : b.message,
          data: scrub(b.data) as typeof b.data,
        }));
      }

      // Scrub error messages themselves (emails/phones embedded in thrown errors).
      if (event.message) event.message = redactString(event.message);
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map((ex) => ({
          ...ex,
          value: ex.value ? redactString(ex.value) : ex.value,
        }));
      }

      return event;
    },

    beforeSendTransaction(event) {
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.query_string;
      }
      return event;
    },
  });
}

/**
 * Identify a user in Sentry using ONLY an opaque internal id.
 * Never pass names, emails, or profile data.
 */
export function setSentryUser(anonymousId: string | null) {
  if (!anonymousId) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: anonymousId });
}

export { Sentry };
